import React, { useRef } from 'react';
import { FocusTargetValueOrFalse } from 'focus-trap';
import { AcceptDeclineForm } from '../../../_lib/Form2/standardForms/AcceptDeclineForm/AcceptDeclineForm';
import { useSonTranslation } from '../../../translation/useSonTranslation';
import ModalDialog from '../../../components/organisms/ModalDialog/ModalDialog';
import { SharedFile } from '../SharedFiles.types';

type SharedFilesItemDeleteForm = {
  closeModal: () => void;
  file: SharedFile;
};

/**
 * Form to delete a single shared file.
 */
export function SharedFilesItemDeleteForm({ closeModal, file }: SharedFilesItemDeleteForm) {
  const { t } = useSonTranslation('SharedFiles');
  const indexInRow = useRef<number | undefined>(undefined);

  const getAllFocusableLinks = () => document.querySelectorAll('[data-shared-files-focusable]');

  // determine the current index of this file in the row
  const getIndexInRow = () => {
    const elements = getAllFocusableLinks();
    indexInRow.current = Array.from(elements).findIndex(e => e.getAttribute('data-shared-files-focusable') === file.id);
  };

  /**
   * After successfull deletion determine which element to focus next -> current item and the modal will disappear.
   * All items that are focusable should have a data-shared-files-focusable attribute.
   */
  const focusAfterDelete = (prevFocus: HTMLElement | SVGElement): FocusTargetValueOrFalse => {
    const elements = getAllFocusableLinks();

    if (indexInRow.current) {
      const newAt = elements.item(indexInRow.current) as HTMLElement;
      const previous = elements.item(indexInRow.current - 1) as HTMLElement;
      // the last prevfocus here should be the upload button
      return newAt || previous || prevFocus;
    }
    return prevFocus;
  };

  return (
    <ModalDialog
      closeModal={closeModal}
      headline={t('forms.delete.modalHeadline', { name: file.fileTitle || file.fileName })}
      initialFocusIndex={1}
      focusOnReturn={focusAfterDelete}
    >
      <AcceptDeclineForm
        form={file.forms!.delete!}
        headline={t('forms.delete.headline', { name: file.fileTitle || file.fileName })}
        messageSuccess={t('forms.delete.success')}
        messageSending={t('forms.delete.sending')}
        messageFail={t('forms.delete.fail')}
        onSuccess={() => {
          getIndexInRow();
        }}
      />
    </ModalDialog>
  );
}
